import awsconfig from "../config/aws-exports";
import { normaliseFuelType, normaliseMeterType } from "../constants/tariffInfo";
import HttpError from "./httpError";

export default class Client {
  constructor(
    key = awsconfig.api_key,
    base_url = awsconfig.switching_endpoint
  ) {
    this.key = key;
    this.base_url = base_url;
    this.session_id = null;
  }

  get headers() {
    return {
      "x-api-key": this.key,
      "Content-Type": "application/json",
    };
  }

  async fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      headers: this.headers,
      ...options,
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {};

    if (!response.ok) {
      throw new HttpError(`HTTP error! status: ${response.status}`, response.status, data);
    }

    return data;
  }

  checkSessionId() {
    if (this.session_id) return;

    if (localStorage.getItem("session_id")) {
      this.session_id = localStorage.getItem("session_id");
    } else {
      throw new Error("No session ID found");
    }
  }

  async createSession(email, firstName, lastName) {
    const session_id = await this.fetchJSON(
      `${this.base_url}/session?email=${encodeURIComponent(
        email
      )}&first_name=${encodeURIComponent(
        firstName
      )}&last_name=${encodeURIComponent(lastName)}`,
      {
        method: "PUT",
      }
    );

    this.session_id = session_id;
    localStorage.setItem("session_id", session_id);

    return session_id;
  }

  async lookupAddress(postcode) {
    this.checkSessionId();

    return this.fetchJSON(
      `${this.base_url}/session/${
        this.session_id
      }/address-search?postcode=${encodeURIComponent(postcode.replaceAll(/\s+/g, ""))}`
    );
  }

  async getMeterDetails(addressId, data) {
    this.checkSessionId();

    const body = JSON.stringify({ accepted_terms_and_conditions: data });

    return this.fetchJSON(
      `${this.base_url}/session/${
        this.session_id
      }/meter-details?address_id=${encodeURIComponent(addressId)}`,
      {
        method: "POST",
        body,
      }
    );
  }

  async getSuppliersList(dnoId, fuelType, meterType, paymentType) {
    this.checkSessionId();

    if (!dnoId || !fuelType || !meterType || !paymentType) {
      return [];
    }

    const queryParams = new URLSearchParams();
    queryParams.append("dno_id", dnoId);
    queryParams.append("fuel_type", normaliseFuelType(fuelType));
    queryParams.append("meter_type", normaliseMeterType(meterType));
    if (paymentType === "Pay On Receipt Of Bill") {
      queryParams.append("payment_method", "Cash or cheque");  // Perse require this value for Pay On Receipt Of Bill
    }
    else {
      queryParams.append("payment_method", paymentType);
    }

    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/suppliers?${queryParams.toString()}`
    );
  }

  async getQuotesRequest(mpan, mprn = null) {
    this.checkSessionId();

    const body = JSON.stringify({ mpan, ...(mprn && { mprn }) });

    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/quotes-request`,
      {
        method: "POST",
        body,
      }
    );
  }

  async switchValidationSchema(supplierId, switchMode) {
    this.checkSessionId();

    return this.fetchJSON(
      `${this.base_url}/session/${
        this.session_id
      }/switch-validation-schema?supplier_id=${encodeURIComponent(
        supplierId
      )}&switch_mode=${encodeURIComponent(switchMode)}`
    );
  }

  async getQuotes(data) {
    if (!data || !data?.mpan) { 
      return
    }
    this.checkSessionId();

    if (data?.meterTypeElec?.endsWith("-SMART")) {
      data.elecIsSmart = true;
    } else {
      data.elecIsSmart = false;
    }
    if (data?.meterTypeGas?.endsWith("-SMART")) {
      data.gasIsSmart = true;
    } else {
      data.gasIsSmart = false;
    }
    data.meterTypeElec = normaliseMeterType(data.meterTypeElec);
    data.meterTypeGas = normaliseMeterType(data.meterTypeGas);

    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/generate-quotes`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }

  async generateSwitch(data) {
    this.checkSessionId();

    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/generate-switch`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  }

  async switchStatus() {
    this.checkSessionId();

    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/switch-status`
    );
  }

  async getSupplierTariffs(supplierId, dnoId) {
    this.checkSessionId();

    if (!supplierId || !dnoId) {
      return [];
    }
  
    const queryParams = new URLSearchParams();
    queryParams.append('dno_id', dnoId);
    queryParams.append('supplier_id', supplierId);
    
    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/tariffs?${queryParams.toString()}`
    );
  }

  async subscribeContactToEmail() {
    this.checkSessionId();
    return this.fetchJSON(
      `${this.base_url}/session/${this.session_id}/email-subscription`,
      {
        method: "POST",
      }
    );
  }
}
